import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";
import { getStripeConnectStatus } from "../stripe/stripeApi";
import {
  getEmailAutomationStatus,
  getEmailSequence,
} from "../email/emailAutomationApi";
import {
  describeGroup,
  evaluatePersonalization,
  hasRequiredLeak,
  type GroupKey,
  type Leak,
} from "./personalizationSpec";

// Client-side port of the MCP launch-readiness gates
// (mcp/src/repos/launchReadiness.ts). Same ready/partial/missing semantics so
// the coach's checklist matches the AI's get_launch_readiness. Each gate maps
// to an admin route the coach can click, instead of the MCP tool name the
// server reports. Reads only; no secret values are ever returned.

export type CheckStatus = "ready" | "partial" | "missing" | "unknown";

export type LaunchCheck = {
  key: string;
  item: string;
  status: CheckStatus;
  detail: string;
  // Admin route to fix the gap. Absent for manual/live-store gates.
  route?: string;
  // Shown but never scored (live store state: listings, screenshots, release).
  manual?: boolean;
};

export type LaunchGroup = {
  title: string;
  checks: LaunchCheck[];
};

// Per-field personalization roll-up, mirroring the MCP payload.
export type LaunchPersonalization = {
  authored: number;
  total: number;
  percent: number;
  leaks: Leak[];
  leaksTruncated?: boolean;
};

export type LaunchReadiness = {
  score: { ready: number; total: number; percent: number };
  groups: LaunchGroup[];
  complete: boolean;
  personalization: LaunchPersonalization;
};

// A brand new community leaks every field. Cap the listed leaks so the payload
// stays small; the counts and the section details carry the rest.
const MAX_LEAKS = 40;

// Where the coach fixes each personalization group. The admin router is flat,
// so questionnaire, app copy, paywall and community config all live in
// /settings.
const GROUP_ROUTE: Record<GroupKey, string> = {
  questionnaire: "/settings",
  permissionConsents: "/settings",
  appCopy: "/settings",
  paywall: "/settings",
  community: "/settings",
  author: "/authors",
};

const check = (
  key: string,
  item: string,
  status: CheckStatus,
  detail: string,
  route?: string,
  manual?: boolean
): LaunchCheck => ({
  key,
  item,
  status,
  // describeGroup returns "" when a group scored no fields, so trim the
  // trailing space that leaves on a detail string.
  detail: detail.trim(),
  route,
  manual,
});

// Best-effort wrapper: a failed read becomes null and its gate reports
// "unknown" rather than blanking the whole checklist.
const safe = async <T>(fn: () => Promise<T>): Promise<T | null> => {
  try {
    return await fn();
  } catch {
    return null;
  }
};

const countWhere = async (
  collection: string,
  communityId: string
): Promise<number> => {
  const snap = await firestore
    .collection(collection)
    .where("communityId", "==", communityId)
    .get();
  return snap.size;
};

const resolveSlug = (communityId: string, community: any): string =>
  String(community?.slug || communityId)
    .trim()
    .toLowerCase();

export async function computeLaunchReadiness(
  communityId: string,
  community: any
): Promise<LaunchReadiness> {
  const communityDoc = firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId);

  const [
    authorDocs,
    appCopy,
    habitCount,
    planCount,
    quoteCount,
    questionnaire,
    paywall,
    shop,
    distribution,
    landingLive,
    stripe,
    email,
    welcome,
    winback,
  ] = await Promise.all([
    // Full documents, not a count: the personalization evaluator scores the
    // author fields. The web SDK has no .count(), so keep the .get() pattern.
    safe(async () => {
      const snap = await firestore
        .collection(DB_PATH.AUTHOR)
        .where("communityId", "==", communityId)
        .get();
      return snap.docs.map((d) => d.data() as Record<string, any>);
    }),
    safe(() => communityDoc.collection("AppCopy").doc("content").get()),
    safe(() => countWhere(DB_PATH.HABIT, communityId)),
    safe(() => countWhere(DB_PATH.PLAN, communityId)),
    safe(() => countWhere(DB_PATH.QUOTE, communityId)),
    safe(() =>
      communityDoc
        .collection(DB_PATH.ONBOARDING)
        .doc("questionnaire_metadata")
        .get()
    ),
    safe(() => communityDoc.collection("landingPage").doc("info").get()),
    safe(() => communityDoc.collection("Shop").doc("shop").get()),
    safe(() =>
      communityDoc.collection(DB_PATH.DISTRIBUTION).doc("credentials").get()
    ),
    safe(async () => {
      const slug = resolveSlug(communityId, community);
      const snap = await firestore.collection("PublicLanding").doc(slug).get();
      return snap.exists;
    }),
    safe(() => getStripeConnectStatus(communityId)),
    safe(() => getEmailAutomationStatus(communityId)),
    safe(() => getEmailSequence(communityId, "welcome")),
    safe(() => getEmailSequence(communityId, "winback")),
  ]);

  // ── Content ────────────────────────────────────────────────────────────
  const c = (community || {}) as Record<string, any>;
  const authorCount = authorDocs ? authorDocs.length : null;
  const brandingMissing = ["title", "description", "primaryColor"].filter(
    (f) => !c?.[f]
  );
  const content: LaunchCheck[] = [];

  // Document data the evaluator needs. Derived up here because several gates
  // below read the same values.
  const copy = appCopy?.exists ? (appCopy.data() as Record<string, any>) : {};
  const qData = questionnaire?.exists
    ? (questionnaire.data() as Record<string, any>)
    : null;
  const pw = paywall?.exists ? (paywall.data() as Record<string, any>) : null;

  // Per-field personalization. The app falls back to a generic translated
  // string for any field it finds empty, so an unset field is a leak even
  // when its section exists. See personalizationSpec.ts.
  const personalization = evaluatePersonalization({
    questionnaire: qData,
    appCopy: copy,
    paywall: pw,
    community: c,
    authors: authorDocs,
  });

  const personalizationFields = ["fontTheme", "stylePack", "entryValueProps"];
  const personalized = personalizationFields.filter((field) => {
    if (field === "entryValueProps") {
      return Array.isArray(c.entryValueProps?.items) && c.entryValueProps.items.length > 0;
    }
    return Boolean(c[field]);
  });
  const missingPersonalization = personalizationFields.filter(
    (field) => !personalized.includes(field)
  );
  const communityFields = personalization.byGroup.community;
  content.push(
    personalized.length === personalizationFields.length &&
      !hasRequiredLeak(communityFields)
      ? check("personalization", "App personalization", "ready", `Typography, visual style and entry value props set. ${describeGroup(communityFields)}`, GROUP_ROUTE.community)
      : personalized.length
        ? check("personalization", "App personalization", "partial", `${
            missingPersonalization.length
              ? `Still to set: ${missingPersonalization.join(", ")}.`
              : "Typography, visual style and entry value props set."
          } ${describeGroup(communityFields)}`, GROUP_ROUTE.community)
        : check("personalization", "App personalization", "missing", "Set fontTheme, stylePack and entryValueProps.", GROUP_ROUTE.community)
  );

  const scoreCalculator = (c.insightScore || {}) as Record<string, any>;
  const scoreMetrics = Array.isArray(scoreCalculator.metrics)
    ? scoreCalculator.metrics
    : [];
  const scoreSourceTypes = new Set([
    "learning_plan_step_completed",
    "habit_completed",
    "session_logged",
    "task_completed",
    "streak_days",
    "mood_score",
    "steps",
    "active_calories",
    "sleep_hours",
  ]);
  const scoreNameReady =
    typeof scoreCalculator.scoreName === "string" &&
    /\bscore$/i.test(scoreCalculator.scoreName.trim());
  const scoreMetricIds = scoreMetrics.map((metric: Record<string, any>) =>
    String(metric?.id || "").trim()
  );
  const scoreMetricsReady =
    scoreMetrics.length > 0 &&
    new Set(scoreMetricIds).size === scoreMetricIds.length &&
    scoreMetrics.every(
      (metric: Record<string, any>) =>
        typeof metric?.id === "string" &&
        metric.id.trim().length > 0 &&
        /^[a-z0-9][a-z0-9_-]*$/.test(metric.id.trim()) &&
        typeof metric?.label === "string" &&
        metric.label.trim().length > 0 &&
        typeof metric?.description === "string" &&
        metric.description.trim().length > 0 &&
        (metric.weight == null ||
          (typeof metric.weight === "number" && metric.weight > 0)) &&
        Array.isArray(metric.sources) &&
        metric.sources.length > 0 &&
        metric.sources.every(
          (source: Record<string, any>) =>
            typeof source?.type === "string" &&
            scoreSourceTypes.has(source.type) &&
            typeof source?.target === "number" &&
            source.target > 0 &&
            (source.weight == null ||
              (typeof source.weight === "number" && source.weight > 0))
        )
    );
  const scoreEnabled = scoreCalculator.enabled === true;
  const scoreVersionReady = scoreCalculator.scoreVersion === 1;
  const explicitlyConfigured =
    typeof scoreCalculator._configuredAt === "string" &&
    scoreCalculator._configuredAt.trim().length > 0;
  const scoreCalculatorParts = [
    scoreNameReady,
    scoreMetricsReady,
    scoreEnabled,
    scoreVersionReady,
    explicitlyConfigured,
  ].filter(Boolean).length;
  content.push(
    !scoreEnabled
      ? check(
          "score-calculator",
          "Score calculator",
          "ready",
          "Automatic scoring is intentionally disabled for this community.",
          "/settings"
        )
      : scoreCalculatorParts === 5
      ? check(
          "score-calculator",
          "Score calculator",
          "ready",
          `${scoreCalculator.scoreName}: v1 with ${scoreMetrics.length} configured metric(s).`,
          "/settings"
        )
      : check(
          "score-calculator",
          "Score calculator",
          "partial",
          `${scoreCalculatorParts}/5 requirements ready. Complete the enabled v1 logged-activity calculator in Settings or ask the MCP to run configure_score_calculator.`,
          "/settings"
        )
  );

  const coverReady = Boolean(String(c.coverURL || "").trim());
  const galleryCount = Array.isArray(c.imagesURL)
    ? c.imagesURL.filter(
        (url: unknown) => typeof url === "string" && url.trim().length > 0
      ).length
    : 0;
  content.push(
    coverReady && galleryCount >= 2
      ? check("entry-imagery", "Entry & onboarding imagery", "ready", `Entry cover + ${galleryCount} shared gallery image(s).`, "/settings")
      : coverReady || galleryCount > 0
        ? check("entry-imagery", "Entry & onboarding imagery", "partial", `${coverReady ? "Entry cover set" : "Entry cover missing"}; ${galleryCount}/2 gallery images.`, "/settings")
        : check("entry-imagery", "Entry & onboarding imagery", "missing", "Add an EntryCTA cover and at least 2 shared gallery images.", "/settings")
  );

  const nonEmptyObject = (value: unknown): boolean =>
    !!value && typeof value === "object" &&
    Object.entries(value as Record<string, any>).some(
      ([key, item]) => key !== "i18n" &&
        (typeof item === "string" ? item.trim().length > 0 :
          Array.isArray(item) ? item.length > 0 : nonEmptyObject(item))
    );
  const appCopySections = [
    "learningPrompts",
    "checkInNotifications",
    "createLearning",
    "checkInCard",
    "entryCta",
    "levelNames",
    "levelIcons",
  ];
  const authoredCopy = appCopySections.filter((field) => nonEmptyObject(copy[field]));
  const appCopyFields = personalization.byGroup.appCopy;
  content.push(
    authoredCopy.length === appCopySections.length &&
      !hasRequiredLeak(appCopyFields)
      ? check("app-copy", "App copy", "ready", `All 7 app-copy sections authored. ${describeGroup(appCopyFields)}`, GROUP_ROUTE.appCopy)
      : authoredCopy.length
        ? check("app-copy", "App copy", "partial", `${authoredCopy.length}/${appCopySections.length} app-copy sections authored. ${describeGroup(appCopyFields)}`, GROUP_ROUTE.appCopy)
        : check("app-copy", "App copy", "missing", "Author learning, check-in and mastery copy with the MCP.", GROUP_ROUTE.appCopy)
  );

  content.push(
    !community
      ? check("branding", "Community branding", "missing", "Community not found.", "/settings")
      : brandingMissing.length
        ? check(
            "branding",
            "Community branding",
            "partial",
            `Add ${brandingMissing.join(", ")}.`,
            "/settings"
          )
        : check("branding", "Community branding", "ready", "Name, description and colors set.", "/settings")
  );

  const authorFields = personalization.byGroup.author;
  content.push(
    (authorCount ?? 0) === 0
      ? check("author", "Author", "missing", "No author (the coach/voice).", GROUP_ROUTE.author)
      : hasRequiredLeak(authorFields)
        ? check("author", "Author", "partial", `${authorCount} author(s). ${describeGroup(authorFields)}`, GROUP_ROUTE.author)
        : check("author", "Author", "ready", `${authorCount} author(s). ${describeGroup(authorFields)}`, GROUP_ROUTE.author)
  );

  content.push(
    (habitCount ?? 0) >= 3
      ? check("habits", "Habits", "ready", `${habitCount} habits.`, "/habits")
      : (habitCount ?? 0) > 0
        ? check("habits", "Habits", "partial", `${habitCount} so far. Aim for about 5.`, "/habits")
        : check("habits", "Habits", "missing", "Add a few habits.", "/habits")
  );

  content.push(
    (planCount ?? 0) > 0
      ? check("plans", "Plans", "ready", `${planCount} plan.`, "/plans")
      : check("plans", "Plans", "missing", "Add a learning plan.", "/plans")
  );

  const qCount = Array.isArray(qData?.questions) ? qData!.questions.length : 0;
  const questionnaireFields = personalization.byGroup.questionnaire;
  content.push(
    !qData
      ? check("questionnaire", "Questionnaire", "missing", "No onboarding questionnaire.", GROUP_ROUTE.questionnaire)
      : qCount > 0
        ? hasRequiredLeak(questionnaireFields)
          ? check("questionnaire", "Questionnaire", "partial", `${qCount} questions. ${describeGroup(questionnaireFields)}`, GROUP_ROUTE.questionnaire)
          : check("questionnaire", "Questionnaire", "ready", `${qCount} questions. ${describeGroup(questionnaireFields)}`, GROUP_ROUTE.questionnaire)
        : check("questionnaire", "Questionnaire", "partial", "Exists but has no questions.", GROUP_ROUTE.questionnaire)
  );

  const notificationConsent = qData?.permissionConsents?.notification as
    | Record<string, any>
    | undefined;
  const consentFields = notificationConsent
    ? [
        notificationConsent.title,
        notificationConsent.subtitle,
        notificationConsent.cta,
        notificationConsent.morning?.message,
        notificationConsent.evening?.message,
      ].filter((value) => String(value || "").trim()).length
    : 0;
  const consentPersonalization = personalization.byGroup.permissionConsents;
  content.push(
    consentFields === 5 && !hasRequiredLeak(consentPersonalization)
      ? check("permission-copy", "Permission consent copy", "ready", `Notification permission copy is complete. ${describeGroup(consentPersonalization)}`, GROUP_ROUTE.permissionConsents)
      : consentFields > 0
        ? check("permission-copy", "Permission consent copy", "partial", `${consentFields}/5 core notification consent fields authored. ${describeGroup(consentPersonalization)}`, GROUP_ROUTE.permissionConsents)
        : check("permission-copy", "Permission consent copy", "missing", "Author notification permission copy; add health/focus copy when enabled.", GROUP_ROUTE.permissionConsents)
  );

  const paywallFields = [
    pw?.title,
    pw?.subtitle,
    pw?.buttonText,
    Array.isArray(pw?.features) && pw.features.length >= 3,
    Array.isArray(pw?.plans) && pw.plans.length > 0,
  ].filter(Boolean).length;
  const paywallPersonalization = personalization.byGroup.paywall;
  content.push(
    !pw
      ? check("paywall", "Paywall", "missing", "No paywall copy.", GROUP_ROUTE.paywall)
      : paywallFields === 5 && !hasRequiredLeak(paywallPersonalization)
        ? check("paywall", "Paywall", "ready", `Core copy + ${pw.plans.length} plan(s). ${describeGroup(paywallPersonalization)}`, GROUP_ROUTE.paywall)
        : check("paywall", "Paywall", "partial", `${paywallFields}/5 core paywall areas complete (title, subtitle, CTA, 3+ features, plans). ${describeGroup(paywallPersonalization)}`, GROUP_ROUTE.paywall)
  );

  content.push(
    shop?.exists
      ? check("shop", "Shop", "ready", "Shop configured.", "/settings")
      : check("shop", "Shop", "missing", "No shop set up.", "/settings")
  );

  content.push(
    (quoteCount ?? 0) >= 5
      ? check("quotes", "Quotes", "ready", `${quoteCount} quotes.`, "/quotes")
      : check("quotes", "Quotes", "partial", `${quoteCount ?? 0} quotes. Aim for about 8.`, "/quotes")
  );

  content.push(
    landingLive
      ? check("landing", "Landing page", "ready", "Public landing is live.", "/settings")
      : check("landing", "Landing page", "missing", "No public landing yet.", "/settings")
  );

  // ── Payments ───────────────────────────────────────────────────────────
  const st = (stripe || {}) as Record<string, any>;
  const checkoutLive =
    !!st.connected &&
    !!st.webhookConfigured &&
    Object.keys(st.priceIds || {}).length > 0;
  const payments: LaunchCheck[] = [
    checkoutLive
      ? check("stripe", "Web checkout", "ready", "Stripe connected with prices.", "/analytics")
      : st.connected
        ? check(
            "stripe",
            "Web checkout",
            "partial",
            st.webhookConfigured ? "Add prices." : "Finish webhook and prices.",
            "/analytics"
          )
        : check("stripe", "Web checkout", "missing", "Connect Stripe to sell on the web.", "/analytics"),
  ];

  // ── Email ──────────────────────────────────────────────────────────────
  const em = (email || {}) as Record<string, any>;
  const welcomeSteps = (welcome as Record<string, any>)?.steps?.length ?? 0;
  const winbackSteps = (winback as Record<string, any>)?.steps?.length ?? 0;
  const emailChecks: LaunchCheck[] = [
    em.connected || em.configured
      ? check("email-sender", "Email sender", "ready", "Sending is set up.", "/messaging")
      : check("email-sender", "Email sender", "missing", "Set up email automation.", "/messaging"),
    welcomeSteps > 0
      ? check("email-welcome", "Welcome emails", "ready", `${welcomeSteps} emails.`, "/messaging")
      : check("email-welcome", "Welcome emails", "missing", "Write a welcome sequence.", "/messaging"),
    winbackSteps > 0
      ? check("email-winback", "Winback emails", "ready", `${winbackSteps} emails.`, "/messaging")
      : check("email-winback", "Winback emails", "missing", "Write a winback sequence.", "/messaging"),
  ];

  // ── App stores ─────────────────────────────────────────────────────────
  const dist = (distribution?.exists ? distribution.data() : {}) as Record<
    string,
    any
  >;
  const credCheck = (
    key: string,
    label: string,
    side: Record<string, any> | undefined
  ): LaunchCheck =>
    side?.status === "valid"
      ? check(key, label, "ready", "Validated.", "/settings")
      : side && Object.keys(side).some((k) => side[k])
        ? check(key, label, "partial", `Status: ${side.status || "not validated"}.`, "/settings")
        : check(key, label, "missing", "Not configured.", "/settings");

  const stores: LaunchCheck[] = [
    credCheck("apple-creds", "Apple credentials", dist.apple),
    credCheck("google-creds", "Google credentials", dist.google),
    /^https:\/\/\S+$/i.test(String(c.privacyPolicyUrl || "").trim())
      ? check(
          "privacy-policy",
          "Privacy policy",
          "ready",
          `Live URL saved: ${String(c.privacyPolicyUrl).trim()}`,
          undefined,
          true
        )
      : check(
          "privacy-policy",
          "Privacy policy",
          "missing",
          "Save the live policy URL used by both stores. Transferred apps need a dedicated policy naming the current developer.",
          undefined,
          true
        ),
    check(
      "store-listings",
      "Store listings and screenshots",
      "unknown",
      "Prepare in App Store Connect and Google Play.",
      undefined,
      true
    ),
    check(
      "release",
      "Build and release",
      "unknown",
      "Submit the app for review from the stores.",
      undefined,
      true
    ),
  ];

  const groups: LaunchGroup[] = [
    { title: "Content", checks: content },
    { title: "Payments", checks: payments },
    { title: "Email", checks: emailChecks },
    { title: "App stores", checks: stores },
  ];

  // Score everything except manual and unknown gates.
  const scored = groups
    .flatMap((g) => g.checks)
    .filter((x) => !x.manual && x.status !== "unknown");
  const ready = scored.filter((x) => x.status === "ready").length;
  const total = scored.length;
  const percent = Math.round((ready / Math.max(total, 1)) * 100);

  return {
    score: { ready, total, percent },
    groups,
    complete: total > 0 && ready === total,
    personalization: {
      authored: personalization.authored,
      total: personalization.total,
      percent: personalization.percent,
      // Required leaks first so the cap never hides what moves the score.
      leaks: [...personalization.leaks]
        .sort((a, b) => Number(b.required) - Number(a.required))
        .slice(0, MAX_LEAKS),
      ...(personalization.leaks.length > MAX_LEAKS
        ? { leaksTruncated: true }
        : {}),
    },
  };
}
